// A date-only value (a due date, a review date, a scheduled day, a target date) is a calendar
// date, not an instant. It travels to the API as UTC midnight, fills <input type="date"> from its
// UTC date and is labelled in UTC, so the box, the label and the saved value name the same day in
// every time zone. Re-saving an unchanged form therefore never moves the date. Real instants (an
// incident's awareAt, a datetime-local box) keep lib/format.ts fmtDateTime and local time.

type DateLike = string | Date | null | undefined;

const YMD = /^\d{4}-\d{2}-\d{2}$/;

/** Stored value → <input type="date"> value (yyyy-mm-dd, the UTC date), "" when empty. */
export function toDateInput(v: DateLike): string {
  if (!v) return "";
  if (typeof v === "string" && YMD.test(v)) return v;
  const d = v instanceof Date ? v : new Date(v);
  return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
}

/**
 * <input type="date"> value → ISO string at UTC midnight, or null when empty / invalid.
 * `endOfDay` sends 23:59:59.999Z of the same UTC date instead, for a deadline that means the
 * whole of the chosen day ("due 3 Oct"); the date the box, the label and the API read is unchanged.
 */
export function dateInputToIso(v: string | null | undefined, opts: { endOfDay?: boolean } = {}): string | null {
  if (!v || !YMD.test(v)) return null;
  const d = new Date(`${v}T${opts.endOfDay ? "23:59:59.999" : "00:00:00.000"}Z`);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

/** Label for a date-only value: 25 Sep 2026, read in UTC. A missing value is an em dash. */
export function fmtDateOnly(v: DateLike, opts: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" }): string {
  if (!v) return "—";
  const d = v instanceof Date ? v : new Date(typeof v === "string" && YMD.test(v) ? `${v}T00:00:00Z` : v);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB", { ...opts, timeZone: "UTC" });
}

/** A moment's day in the user's own calendar, as an <input type="date"> value. */
export function localDateInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Today (or `offsetDays` from today) in the user's own calendar, as an <input type="date"> value. */
export function todayInput(offsetDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return localDateInput(d);
}
